import { initNavigation } from './ui/navigation.js';
import { initLoginSteps } from './login/steps.js';
import { initSSO } from './login/sso.js';
import { initDropdowns } from './ui/dropdown.js';
import { updateChurchUI, loadChurches, savePreferences } from './church/church.js';
import { state } from './state.js';
import { initFAB } from './ui/fab.js';
import { renderTopics } from './attendance/topics.js';
import { renderChildrenList } from './attendance/children.js';
import { initReport } from './attendance/report.js';
import { initAdmin } from './admin/admin.js';

// 1. Service Worker — only in production (not localhost/dev) and only from within /cgs/ scope
if ('serviceWorker' in navigator && window.location.hostname !== 'localhost' && window.location.pathname.startsWith('/cgs')) {
    navigator.serviceWorker.register('/sw.js', { scope: '/cgs/' }).then(reg => {
        // Detect when a new SW version finishes installing
        reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    // A new version is ready — activate it immediately
                    newWorker.postMessage({ type: 'SKIP_WAITING' });
                }
            });
        });
    });

    // When the new SW takes control, reload once to serve fresh assets
    let swRefreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!swRefreshing) {
            swRefreshing = true;
            location.reload();
        }
    });
}

// 2. Offline mode — disables DB-bound UI elements via CSS class on body
const applyOfflineMode = (offline) => document.body.classList.toggle('offline', offline);
window.addEventListener('online',  () => applyOfflineMode(false));
window.addEventListener('offline', () => applyOfflineMode(true));
applyOfflineMode(!navigator.onLine);

document.addEventListener('DOMContentLoaded', async() => {

    // --- GLOBAL UI COMPONENTS ---
    initNavigation();
    initDropdowns();

    // --- Global Settings (AppSettings only present on pages that include header.php / login.php) ---
    const URI_ROOT      = window.AppSettings?.uriRoot;
    const URI_GOOGLE    = window.AppSettings?.uriGoogle;
    const URI_MICROSOFT = window.AppSettings?.uriMicrosoft;

    // --- PAGE-SPECIFIC LOGIC ---
    // --- ADMIN LOGIC ---
    if (document.getElementById('adminPage')) {
        initAdmin();
        return;
    }

    // --- LOGIN LOGIC ---
    const loginForm = document.getElementById('loginForm');
    if (loginForm) {
        // Only if the form exists, look for the rest of the elements
        const emailInput = document.getElementById('email');
        const continueBtn = document.getElementById('continueBtn');
        const backBtn = document.getElementById('backBtn');
        const passwordInput = document.getElementById('password');
        const emailPreview = document.getElementById('emailPreview');
        const googleBtn = document.querySelector('.btn-google');
        const microsoftBtn = document.querySelector('.btn-microsoft');

        // Initialize login modules
        initLoginSteps({
            form: loginForm,
            continueBtn,
            backBtn,
            emailInput,
            passwordInput,
            emailPreview,
            forgotPasswordLink: document.getElementById('forgotPasswordLink'),
        });

        initSSO(googleBtn, URI_GOOGLE, 'Connecting to Google...');
        initSSO(microsoftBtn, URI_MICROSOFT, 'Connecting to Microsoft...');

        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('login_error')) {
            alert("Authentication failed. Please try again.");
        }
    }

    // --- DASHBOARD LOGIC ---
    const churchSelector = document.getElementById('churchSelector');
    if (churchSelector) {
        try {
            // 1. Single request for ALL data
            const response = await fetch(`${URI_ROOT}ajax/get_master`, { cache: 'no-store' });
            const masterJSON = await response.json();

            // 2. Save to global state
            state.init(masterJSON);

            // 3. Set up church selector
            churchSelector.addEventListener('click', (e) => {
                const item = e.target.closest('.c-dropdown__item');
                if (item) {
                    updateChurchUI(item.dataset.id);
                }
            });

            // 4. Initial load — restore last view before rendering
            const prefs = masterJSON.user;
            if (prefs.last_view === 'children') {
                document.querySelector('.topics-column')?.classList.add('show-children');
                const icon = document.getElementById('headerViewIcon');
                if (icon) icon.className = 'bi bi-card-list fs-4';
            }
            loadChurches({
                churchId: prefs.last_church_id ? Number(prefs.last_church_id) : null,
                groupId:  prefs.last_group_id  ? Number(prefs.last_group_id)  : null,
            });
            initFAB();
            initReport();

            // 5. View toggle (topics ↔ children)
            document.getElementById('headerViewToggleBtn')?.addEventListener('click', (e) => {
                e.preventDefault();
                const topicsColumn = document.querySelector('.topics-column');
                const icon = document.getElementById('headerViewIcon');
                if (!topicsColumn || !icon) return;

                if (topicsColumn.classList.contains('show-children')) {
                    history.back(); // popstate handles the rest
                } else {
                    renderChildrenList(state.currentGroup);
                    topicsColumn.classList.add('show-children');
                    icon.className = 'bi bi-card-list fs-4';
                    history.pushState({ page: 'children' }, '', location.pathname);
                    savePreferences();
                }
            });
        } catch (error) {
            console.error("Error loading master data:", error);
        }
    }

    // Mark the base history entry so popstate can detect "back to app"
    history.replaceState({ page: 'app' }, '');

    // If children view was restored from saved preferences, sync the history stack
    // so that history.back() from the toggle button lands on { page: 'app' }, not on a previous page
    if (document.querySelector('.topics-column')?.classList.contains('show-children')) {
        history.pushState({ page: 'children' }, '', location.pathname);
    }

    window.addEventListener('popstate', (event) => {
        const page = event.state?.page;

        // Closing a modal (back from modal → may land on 'children' or 'app')
        document.querySelectorAll('.is-visible').forEach(m => m.classList.remove('is-visible'));
        document.body.style.position = '';
        document.body.style.width = '';

        // Going back from children view to topics
        if (page === 'app') {
            const topicsColumn = document.querySelector('.topics-column');
            if (topicsColumn?.classList.contains('show-children')) {
                topicsColumn.classList.remove('show-children');
                const icon = document.getElementById('headerViewIcon');
                if (icon) icon.className = 'bi bi-people fs-4';
                renderTopics(state.currentGroup);
                savePreferences();
            }
        }
    });
});